import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Settings, RefreshCw, AlertTriangle, Save, RotateCcw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTH_KEY = 'adminAuth';

/**
 * Card admin para controlar o contador exibido na Landing Page.
 * Persiste no banco (não depende de redeploy).
 * - combo_seed_offset: soma simbólica adicionada às vendas reais
 * - combo_total: total de vagas exibido (denominador)
 */
export default function LandingSettingsCard({ onSaved }) {
  const [settings, setSettings] = useState({ combo_seed_offset: 0, combo_total: 100 });
  const [stats, setStats] = useState({ combo_real_count: 0, combo_count: 0, combo_remaining: 100 });
  const [draft, setDraft] = useState({ combo_seed_offset: 0, combo_total: 100 });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, statsRes] = await Promise.all([
        axios.get(`${API}/vendas-eleicoes/settings`),
        axios.get(`${API}/vendas-eleicoes/stats`),
      ]);
      setSettings(sRes.data);
      setDraft(sRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Erro ao carregar config da landing:', err);
      setFeedback({ type: 'error', text: 'Erro ao carregar configurações' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-dismiss feedback
  useEffect(() => {
    if (!feedback) return undefined;
    const t = setTimeout(() => setFeedback(null), 3500);
    return () => clearTimeout(t);
  }, [feedback]);

  const previewOffset = Number(draft.combo_seed_offset) || 0;
  const previewTotal = Math.max(1, Number(draft.combo_total) || 1);
  const previewCount = Math.min(previewTotal, stats.combo_real_count + Math.max(0, previewOffset));
  const previewRemaining = Math.max(0, previewTotal - previewCount);

  // Nível de urgência do preview (mesmo cálculo da landing)
  const urgencyLevel = previewRemaining === 0 ? 'sold-out'
    : previewRemaining <= 10 ? 'critical'
    : previewRemaining <= 25 ? 'high'
    : previewRemaining <= 50 ? 'medium'
    : 'low';
  const urgencyStyle = {
    'low':      { text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', bar: 'from-emerald-500 to-amber-500', label: `${previewRemaining} vagas disponíveis` },
    'medium':   { text: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200',   bar: 'from-amber-500 to-orange-500', label: `Restam ${previewRemaining} vagas` },
    'high':     { text: 'text-orange-700',  bg: 'bg-orange-50',  border: 'border-orange-200',  bar: 'from-orange-500 to-red-500',   label: `Últimas ${previewRemaining} vagas` },
    'critical': { text: 'text-red-700',     bg: 'bg-red-50',     border: 'border-red-200',     bar: 'from-red-500 to-rose-600',     label: `URGENTE: ${previewRemaining} vagas restantes!` },
    'sold-out': { text: 'text-slate-700',   bg: 'bg-slate-100',  border: 'border-slate-300',   bar: 'from-slate-500 to-slate-600',  label: 'Vagas esgotadas' },
  }[urgencyLevel];

  const percent = Math.min(100, Math.round((previewCount / previewTotal) * 100));
  const isDirty = draft.combo_seed_offset !== settings.combo_seed_offset || draft.combo_total !== settings.combo_total;

  const save = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const token = sessionStorage.getItem(AUTH_KEY);
      const payload = {
        combo_seed_offset: Math.max(0, Number(draft.combo_seed_offset) || 0),
        combo_total: Math.max(1, Number(draft.combo_total) || 1),
      };
      const res = await axios.put(`${API}/vendas-eleicoes/settings`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings(res.data);
      setDraft(res.data);
      setFeedback({ type: 'success', text: 'Contador da landing atualizado. Alteração já visível ao público.' });
      // Também atualiza stats para refletir novo real+offset
      const statsRes = await axios.get(`${API}/vendas-eleicoes/stats`);
      setStats(statsRes.data);
      onSaved?.();
    } catch (err) {
      console.error('Erro ao salvar config:', err);
      const detail = err.response?.data?.detail;
      setFeedback({ type: 'error', text: typeof detail === 'string' ? detail : 'Erro ao salvar. Verifique se sua sessão ainda está ativa.' });
    } finally {
      setSaving(false);
    }
  };

  const resetToZero = () => setDraft({ combo_seed_offset: 0, combo_total: 100 });

  return (
    <Card className="mb-6 border-2 border-indigo-200" data-testid="landing-settings-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Settings className="w-5 h-5 text-indigo-500" />
          Contador da Landing Page
          <span className="text-xs font-normal text-gray-500">· ajusta o que o público vê</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6 text-gray-500">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Carregando…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="text-xs uppercase font-semibold text-gray-600">Vagas em lote (offset simbólico)</Label>
                <Input
                  type="number"
                  min={0}
                  value={draft.combo_seed_offset}
                  onChange={(e) => setDraft({ ...draft, combo_seed_offset: e.target.value })}
                  data-testid="settings-offset-input"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Somado às vendas reais no contador da landing. Vendas reais confirmadas contam por cima deste número.
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold text-gray-600">Total de vagas (denominador)</Label>
                <Input
                  type="number"
                  min={1}
                  value={draft.combo_total}
                  onChange={(e) => setDraft({ ...draft, combo_total: e.target.value })}
                  data-testid="settings-total-input"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Total exibido (ex: &quot;X/100&quot;). Reduza para amplificar a sensação de urgência.
                </p>
              </div>
            </div>

            {/* Preview */}
            <div className={`rounded-lg border-2 ${urgencyStyle.border} ${urgencyStyle.bg} p-4 mb-4`} data-testid="settings-preview">
              <p className="text-xs uppercase font-bold tracking-wider text-gray-500 mb-2">Preview do que o público vai ver</p>
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className={`inline-flex items-center gap-1.5 text-sm font-bold ${urgencyStyle.text}`}>
                  <AlertTriangle className="w-4 h-4" />
                  {urgencyStyle.label}
                </span>
                <span className="text-sm text-gray-700 font-mono">
                  <strong>{previewCount}</strong>/{previewTotal} vendidas
                </span>
              </div>
              <div className="relative h-2.5 bg-white/70 rounded-full overflow-hidden border border-gray-200">
                <div
                  className={`h-full bg-gradient-to-r ${urgencyStyle.bar} transition-all duration-500 ease-out`}
                  style={{ width: `${Math.max(2, percent)}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Vendas reais confirmadas hoje: <strong className="text-gray-700">{stats.combo_real_count}</strong> · Offset: <strong className="text-gray-700">{previewOffset}</strong> · Total exibido: {previewCount} = min({previewTotal}, {stats.combo_real_count} + {previewOffset})
              </p>
            </div>

            {feedback && (
              <div
                data-testid="settings-feedback"
                className={`mb-4 p-3 rounded-lg text-sm ${feedback.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}
              >
                {feedback.text}
              </div>
            )}

            <div className="flex flex-wrap gap-2 justify-end">
              <Button
                variant="outline"
                onClick={resetToZero}
                disabled={saving}
                data-testid="settings-reset-btn"
                title="Volta a mostrar apenas vendas reais (offset=0, total=100)"
              >
                <RotateCcw className="w-4 h-4 mr-1.5" /> Zerar urgência (mostrar só vendas reais)
              </Button>
              <Button
                onClick={save}
                disabled={!isDirty || saving}
                className="bg-indigo-500 hover:bg-indigo-600 text-white"
                data-testid="settings-save-btn"
              >
                {saving
                  ? <><RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> Salvando…</>
                  : <><Save className="w-4 h-4 mr-1.5" /> Salvar e aplicar agora</>
                }
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
